import ConversationService from '@/api/services/ConversationService';
import MessageService from '@/api/services/MessageService';
import { MESSAGE_STATUS } from '@/constants/message';
import Attachment from '@/classes/v2/Attachment';
import Message from '@/classes/v2/Message';

const sendStandardSMS = ({ commit }, message) => {
  return MessageService
    .sendSMS(message)
    .then(data => {
      message.providerMessageId = data.providerMessageId;
      if(message.toList){
        if(message.groupMessage){
            message.to = message.toList.sort().join("");
        } else if(message.toList.length > 0) {
          message.to = data.to;
          delete message.toList;
        }
      }
      commit('upsertMessage', message);
    }, error => {
      message.messageStatus = MESSAGE_STATUS.error;
      commit('upsertMessage', message);
      MessageService.onApiError(error);
    });
}

const sendSegmentedSMS = ({ commit }, message, provider) => {
  if (Message.toUnicode(message.body).length <= provider.maxSmsMessageLength) {
    let successAction = 'upsertMessage';
    if (message.toList) {
      successAction = 'addTmpMessage';
    }
    commit(successAction, message);
  }
  else {
    MessageService
      .getMessagePreview(message, provider.maxSmsMessageLength)
      .then(messages => {
        messages.forEach(messagePiece => {
          if (message.toList) {
            commit('addTmpMessage', { ...message, body: messagePiece });
          }
          else {
            commit('upsertMessage', { ...message, body: messagePiece });
          }
        })
      }, MessageService.onApiError);
  }

  return MessageService
    .sendSMS(message)
    .then(data => data, error => {
      message.messageStatus = MESSAGE_STATUS.error;
      commit('upsertMessage', message);
      MessageService.onApiError(error);
    });
}

const retrieveConversations = ({ commit }) => {
  return ConversationService.getAll().then(conversations => {
    console.log("Set conversations.");
    commit('setConversations', conversations);
  }, ConversationService.onApiError);
};

const retrieveConversationByNumber = ({ commit }, { number, until }) => {
  return ConversationService.getConversationByNumber(number, until).then(conversationMessages => {
    if (conversationMessages?.length) {
      commit('upsertMultipleMessages', { conversationId: number, messages: conversationMessages });
    } else {
      return null;
    }
  }, ConversationService.onApiError);
};

const deleteMessageByRowId = ({ commit }, { rowId, markAsDeleted = true }) => {
  return new Promise((resolve)=> {
    MessageService.deleteMessageByRowId(rowId, markAsDeleted)
      .then(response => {
        if (response.successful) {
          commit('deleteMessageByRowId', { rowId });
        }
        resolve(response);
    }, MessageService.onApiError);
  });
};

const deleteConversationByConversationId = ({ commit }, { conversationId, markAsDeleted = true }) => {
  return new Promise((resolve) => {
    ConversationService.deleteConversationByConversationId(conversationId, markAsDeleted)
      .then(response => {
        if (response.successful) {
          commit('deleteConversationByConversationId', { conversationId, markAsDeleted });
        }
        resolve(response);
      }, MessageService.onApiError);
  });
};

const retrieveAttachments = ({ commit }, { conversationId, attachmentsId }) => {
  return MessageService.getAttachments(attachmentsId).then(attachments => {
    commit('updateMessageAttachments', { conversationId, attachmentsId, attachments });
  }, MessageService.onApiError);
};

const retrieveConversationsByDateRange = (state, {from, to, number}) => {
  return number?
    ConversationService.getConversationByDateRange(from, to, number) :
    MessageService.getMessagesByDateRange(from, to);
};

const sendSMS = ({ commit, rootGetters }, message) => {
  const activeProvider = rootGetters['user/activeProvider'];
  if ( !activeProvider.requiresSegmentation ) {
    return sendStandardSMS({ commit }, message);
  }

  return sendSegmentedSMS({ commit }, message, activeProvider);
};
const sendSegmentedMMS = async ({ commit, rootGetters }, formData, provider) => {
  const base64Attachment = await Attachment.fileToBase64(formData.get('file'));

  const attachment = new Attachment({
    content: base64Attachment,
    contentName: formData.get('file').name,
    contentType: formData.get('file').type
  });

  const rawMessage = {
    body: formData.get('message'),
    originationTime: Date.now(),
    from: rootGetters['user/currentUser'].number,
    attachments: [attachment]
  }

  MessageService
    .getMessagePreview({ body: rawMessage.body }, provider.maxMmsMessageLength)
    .then(messages => {
      messages.forEach((messagePiece, index) => {
        if (formData.get('toList')) {
          commit('addTmpMessage', new Message({
            ...rawMessage,
            body: messagePiece,
            toList: formData.get('toList'),
            attachments: index === 0 ? [attachment] : null
          }));
        }
        else {
          commit('upsertMessage', {
            ...rawMessage,
            body: messagePiece,
            to: formData.get('to'),
            attachments: index === 0 ? [attachment] : null
          });
        }
      });

    }, MessageService.onApiError);

  return MessageService.sendMMS(formData).then(data => data, error => {
    rawMessage.messageStatus = MESSAGE_STATUS.error;
    commit('upsertMessage', rawMessage);
    MessageService.onApiError(error);
  });

};

const sendStandardMMS = async ({ commit, rootGetters }, formData) => {
  const base64Attachment = await Attachment.fileToBase64(formData.get('file'));

  const attachment = new Attachment({
    content: base64Attachment,
    contentName: formData.get('file').name,
    contentType: formData.get('file').type
  });

  const rawMessage = {
    body: formData.get('message'),
    originationTime: Date.now(),
    from: rootGetters['user/currentUser'].number,
    attachments: [attachment]
  }

  return MessageService
    .sendMMS(formData)
    .then(data => {
      rawMessage.providerMessageId = data.providerMessageId;
      if (formData.get('toList')) {
        if(formData.has("groupMessage")){
          rawMessage.toList = formData.get('toList').split(",").sort();
          rawMessage.groupMessage = true;
          rawMessage.to = rawMessage.toList.join("");
          commit('upsertMessage', rawMessage);
        } else {
          rawMessage.to = data.to;
          rawMessage.groupMessage = false;
          delete rawMessage.toList;
          commit('upsertMessage', rawMessage);
        }
      }
      else {
        rawMessage.to = formData.get('to');
        commit('upsertMessage', rawMessage);
      }
    }, error => {
    rawMessage.messageStatus = MESSAGE_STATUS.error;
    commit('upsertMessage', rawMessage);
    MessageService.onApiError(error);
  });

};

const sendMMS = async ({ commit, rootGetters }, formData) => {
  const activeProvider = rootGetters['user/activeProvider'];
  if (!activeProvider.requiresSegmentation) {
    return sendStandardMMS({ commit, rootGetters }, formData)
  }

  return sendSegmentedMMS({ commit, rootGetters }, formData, activeProvider)
};

const filterConversations = ({ commit, rootGetters }, searchString) => {
  return MessageService.search(searchString).then(conversations => {
    commit('filterConversations', { conversations, currentUser: rootGetters['user/currentUser'] });
  }, MessageService.onApiError);
};

const upsertMessage = ({ commit }, rawMessage) => {
  commit('upsertMessage', rawMessage);
};

const setConversationMessages = ({ commit }, { conversationId, messages }) => {
  commit('upsertMultipleMessages', { conversationId, messages });
};

const setActiveConversationId = ({ commit, dispatch, rootGetters }, conversationId) => {
  let settings = rootGetters['preferences/getSettings'];
  settings.interfaceState.currentConversation = conversationId;
  dispatch('preferences/updateSettings', settings, { root: true });
  commit('setActiveConversationId', conversationId);
  commit('clearTmpMessages');
};

const clearUnreadMessagesForActiveConversation = ({ commit }) => {
  commit('clearUnreadMessagesForActiveConversation');
};

const clearUnreadMessagesForNumber = ({ commit }, number) => {
  commit('clearUnreadMessagesForNumber', number);
};

const startConversation = ({ commit, dispatch, getters, rootGetters }, contact) => {
  if (contact) {
    const existingConversation = getters.getConversationById(contact.number);
    if (existingConversation) {
      dispatch('setActiveConversationId', existingConversation.conversationId);
    } else {
      const currentUser = rootGetters['user/currentUser'];
      commit('createConversation', {
        from: currentUser.number,
        fromContact: currentUser,
        to: contact.number,
        toContact: contact
      });
      dispatch('setActiveConversationId', contact.number);
    }
  } else {
    // TODO: New empty Message
    dispatch('setActiveConversationId', null);
  }
};

export default {
  clearUnreadMessagesForActiveConversation,
  clearUnreadMessagesForNumber,
  filterConversations,
  retrieveAttachments,
  retrieveConversations,
  retrieveConversationByNumber,
  retrieveConversationsByDateRange,
  deleteConversationByConversationId,
  deleteMessageByRowId,
  sendSMS,
  sendSegmentedSMS,
  sendStandardSMS,
  sendMMS,
  setActiveConversationId,
  setConversationMessages,
  startConversation,
  upsertMessage
};
